/** PLANȘA DINTR-UN PDF, ADUSĂ CA IMAGINE.
 *
 *  ==========================================================================
 *  DE CE ÎN BROWSER, ȘI NU PE SERVER. Lanțul de import «dintr-o imagine» are,
 *  ÎNAINTE de trimitere, pasul de decupare: omul arată pe planșă CARE tronson
 *  se citește, iar agentului îi pleacă numai bucata aia. Un PDF trimis întreg
 *  ar fi ajuns pe server nedecupat — n-ai ce decupa până nu e randat —, deci
 *  ori se pierdea pasul, ori se mai făcea un drum dus-întors numai ca să se
 *  vadă pagina. Randată aici, pagina intră în FLUXUL DE ACUM ca orice poză:
 *  decupare, calcul, original, trimitere. Nimic în aval nu știe că a fost PDF.
 *
 *  PRIMA PAGINĂ, și atât. Planurile de tronson vin de regulă pe o singură
 *  planșă (19.10.2026: «they're usually single page plans»); un alegător de
 *  pagini s-ar adăuga oricând, înaintea decupării, fără să schimbe nimic de
 *  aici.
 *
 *  FUNDAL ALB, PUS DE MÂNĂ. Pagina unui PDF e transparentă, iar o pânză
 *  transparentă scoasă ca PNG dă linii negre pe nimic: peste ea, agentul ar fi
 *  citit un desen pe fond întunecat.
 *
 *  MĂRIMEA SE PLAFONEAZĂ. O planșă A0 la 200 dpi trece de 9000 px pe latura
 *  lungă, adică zeci de megaocteți de PNG — peste cât primește `originalDin
 *  Fisier` (25 MB) și fără niciun câștig: ce citește agentul e desenul, nu
 *  granulația hârtiei. Se randează la cât cere dpi-ul, dar nu peste `MAX_PX`.
 *  ========================================================================== */
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/** Latura lungă, în pixeli, peste care nu se randează. */
export const MAX_PX = 4000;

/** Cât de des se eșantionează pagina. 200 dpi e ce folosește și citirea de
 *  documente pentru planșe (`importer/dosar/citire`): destul cât cotele scrise
 *  de mână să rămână lizibile. */
export const DPI = 200;

export const ePdf = (f: File) => f.type === "application/pdf"
  || /\.pdf$/i.test(f.name);

/** Prima pagină a PDF-ului, ca fișier PNG — de dat mai departe exact ca o poză
 *  aleasă din galerie. Aruncă dacă fișierul nu se poate citi. */
export async function imagineDinPdf(f: File): Promise<File> {
  const date = new Uint8Array(await f.arrayBuffer());
  const doc = await pdfjs.getDocument({ data: date }).promise;
  try {
    const pag = await doc.getPage(1);
    const laUnu = pag.getViewport({ scale: 1 });
    const cerut = DPI / 72;
    const incape = MAX_PX / Math.max(laUnu.width, laUnu.height);
    const vp = pag.getViewport({ scale: Math.min(cerut, incape) });
    const panza = document.createElement("canvas");
    panza.width = Math.max(1, Math.round(vp.width));
    panza.height = Math.max(1, Math.round(vp.height));
    const ctx = panza.getContext("2d");
    if (!ctx) throw new Error("fără context 2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, panza.width, panza.height);
    await pag.render({ canvas: panza, canvasContext: ctx, viewport: vp }).promise;
    const bucata = await new Promise<Blob | null>(
      (gata) => panza.toBlob(gata, "image/png"));
    if (!bucata) throw new Error("pânza nu s-a putut scoate ca PNG");
    return new File([bucata], f.name.replace(/\.pdf$/i, "") + ".png",
                    { type: "image/png" });
  } finally {
    // Pagina ține memoria PDF-ului deschisă cât trăiește documentul; o planșă
    // mare e de ordinul sutelor de megaocteți.
    void doc.cleanup();
  }
}
